import { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

// Swiper Imports
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCards, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-cards';
import Cta from '../Cta/Cta';

// ── Asset Imports ────────────────────────────────────────────────────────────
// NOTE THE EXTENSIONS — they are not all the same:
//   OurWorks1  – OurWorks35 : .jpeg
//   OurWorks36 – OurWorks46 : .jpg   (converted from HEIC)
//   OurWorks47 – OurWorks52 : .jpeg
// If a build fails with "Failed to resolve import", it is almost always an
// extension mismatch on one of these lines rather than a missing file.

// 1–35 (.jpeg)
import OurWorks1 from '../../../assets/Images/OurWorks1.jpeg';
import OurWorks2 from '../../../assets/Images/OurWorks2.jpeg';
import OurWorks3 from '../../../assets/Images/OurWorks3.jpeg';
import OurWorks4 from '../../../assets/Images/OurWorks4.jpeg';
import OurWorks5 from '../../../assets/Images/OurWorks5.jpeg';
import OurWorks6 from '../../../assets/Images/OurWorks6.jpeg';
import OurWorks7 from '../../../assets/Images/OurWorks7.jpeg';
import OurWorks8 from '../../../assets/Images/OurWorks8.jpeg';
import OurWorks9 from '../../../assets/Images/OurWorks9.jpeg';
import OurWorks10 from '../../../assets/Images/OurWorks10.jpeg';
import OurWorks11 from '../../../assets/Images/OurWorks11.jpeg';
import OurWorks12 from '../../../assets/Images/OurWorks12.jpeg';
import OurWorks13 from '../../../assets/Images/OurWorks13.jpeg';
import OurWorks14 from '../../../assets/Images/OurWorks14.jpg';
import OurWorks15 from '../../../assets/Images/OurWorks15.jpg';
import OurWorks16 from '../../../assets/Images/OurWorks16.jpg';
import OurWorks17 from '../../../assets/Images/OurWorks17.jpg';
import OurWorks18 from '../../../assets/Images/OurWorks18.jpg';
import OurWorks19 from '../../../assets/Images/OurWorks19.jpg';
import OurWorks20 from '../../../assets/Images/OurWorks20.jpg';
import OurWorks21 from '../../../assets/Images/OurWorks21.jpg';
import OurWorks22 from '../../../assets/Images/OurWorks22.jpg';
import OurWorks23 from '../../../assets/Images/OurWorks23.jpg';


// Map images to an array for easy iteration
const importedImages = [
  OurWorks1, OurWorks2, OurWorks3, OurWorks4, OurWorks5, OurWorks6,
  OurWorks7, OurWorks8, OurWorks9, OurWorks10, OurWorks11, OurWorks12,
  OurWorks13, OurWorks14, OurWorks15, OurWorks16, OurWorks17, OurWorks18,
  OurWorks19, OurWorks20, OurWorks21, OurWorks22, OurWorks23,
];

const testimonials = [
  { name: "S. Karthik", text: "The quality of the design is amazing! So clean and perfect. My family is feeling the luxury already.", sub: "Residential, Chennai" },
  { name: "Meena Krishnamurthy", text: "I ordered the bespoke interior package and my home has never been softer or more elegant. Excellent work!", sub: "Penthouse, Bangalore" },
  { name: "Prakash Raj", text: "Great service and fast delivery. The materials are curated very well and are of premium quality. Highly recommend.", sub: "Villa, Hyderabad" },
  { name: "Ananya Iyer", text: "I was impressed with the range of concepts. The luxury breakfast nook designs are a lifesaver on busy mornings.", sub: "Apartment, Kochi" },
  { name: "Vikram G.", text: "The finishing is incredibly fine and fresh. It's now a staple of my lifestyle. Truly timeless design.", sub: "Modern Home, Pune" },
];

const sizes = ['large', 'medium', 'small', 'medium', 'large', 'small'];

// Built from importedImages.length rather than a hard-coded count, so adding or
// removing an image above is all that's needed — the previous version had a
// literal 36 here, which silently drops images the moment the two disagree.
const projects = importedImages.map((image, i) => ({
  id: i + 1,
  image,
  size: sizes[i % sizes.length],
}));

const OurWorks = () => {
  const [hoveredProject, setHoveredProject] = useState(null);
  const [bgColor, setBgColor] = useState('#FAFAFA');
  const headerRef = useRef(null);

  useEffect(() => {
    gsap.fromTo(headerRef.current,
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 1.2, ease: 'power4.out' }
    );
  }, []);

  useEffect(() => {
    if (hoveredProject !== null) {
      setBgColor('#F4F4F4');
    } else {
      setBgColor('#FAFAFA');
    }
  }, [hoveredProject]);

  const getSizeClasses = (size) => {
    switch (size) {
      case 'large': return 'md:col-span-2 md:row-span-2 h-[450px] md:h-full';
      case 'medium': return 'md:col-span-1 md:row-span-2 h-[400px] md:h-full';
      default: return 'md:col-span-1 md:row-span-1 h-[350px] md:h-full';
    }
  };

  return (
    <div className="min-h-screen transition-colors duration-1000 ease-in-out font-sans" style={{ backgroundColor: bgColor }}>
      
      {/* 1. HERO HEADER */}
      <div ref={headerRef} className="pt-24 md:pt-32 pb-12 md:pb-20 px-6 lg:px-12 opacity-0">
        <div className="max-w-7xl mx-auto">
          <span className="block font-montserrat text-[10px] text-red-600 font-semibold uppercase tracking-[0.5em] mb-4 md:mb-6">Portfolio Showcase</span>
          <div className="border-b border-black/10 pb-12">
            <h1 className="font-cormorant text-5xl md:text-6xl lg:text-8xl text-black tracking-tight leading-[1.1] lg:leading-[0.9]">
              <span className="text-4xl md:text-5xl font-extrabold text-red-600">
                Our <span className="text-4xl md:text-5xl font-extrabold text-gray-900">Works</span>
              </span>
            </h1>
          </div>
        </div>
      </div>

      {/* 2. MASONRY GRID */}
      <div className="px-6 lg:px-12 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="flex flex-col md:grid md:grid-cols-2 lg:grid-cols-4 md:grid-flow-row-dense gap-6 md:gap-4 lg:gap-6 md:auto-rows-[220px]" 
            layout
          >
            <AnimatePresence mode="popLayout">
              {projects.map((project, index) => (
                <motion.div
                  key={project.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
                  className={`group relative overflow-hidden bg-zinc-100 shadow-sm transition-all duration-700 rounded-sm w-full ${getSizeClasses(project.size)}`}
                  onMouseEnter={() => setHoveredProject(project.id)}
                  onMouseLeave={() => setHoveredProject(null)}
                >
                  <img
                    src={project.image}
                    alt="Showcase Project"
                    // With 50+ images, eager-loading them all makes the page
                    // crawl on first paint. Load the first eight normally (they
                    // are above the fold) and defer the rest until they are
                    // scrolled near.
                    loading={index < 8 ? 'eager' : 'lazy'}
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-[1.8s] ease-out group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-red-950/40 to-transparent opacity-85 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-700" />
                  
                  <div className="absolute top-6 right-6 text-white opacity-100 md:opacity-0 group-hover:opacity-100 transition-all duration-500">
                    <div className="w-12 h-12 border border-white/20 rounded-full flex items-center justify-center backdrop-blur-md bg-red-600/80 hover:bg-black transition-colors duration-300">
                      <ArrowUpRight size={20} strokeWidth={1} />
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
            <Cta />
    </div>
  );
};

export default OurWorks;
